import { useMsal, useAccount } from '@azure/msal-react';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import _debounce from 'lodash/debounce';
import { default as React, useEffect, type FC } from 'react';
import { event } from 'react-ga';

import type { DaznLink } from '../../api/interfaces';

import { LinkListHead } from './LinkListHead';
import { LinkListRow } from './LinkListRow';
interface Props {
  isLoading: boolean;
  links: DaznLink[];
  isActiveSearch: boolean;
  setIsActiveSearch: (isActiveSearch: boolean) => void;
}

const DEBOUNCE_MS = 300;

export const LinkList: FC<Props> = ({ isLoading, links, isActiveSearch, setIsActiveSearch }: Props) => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0]);

  const [linkCopied, setLinkCopied] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterByCurrentUser, setFilterByCurrentUser] = React.useState(false);
  const lastSearchTerm = React.useRef(searchTerm);

  const [visibleLinks, setVisibleLinks] = React.useState(links);
  const debouncedVisibleLinksFilter = React.useRef(
    _debounce((term: string, byCurrentUser: boolean, allLinks: DaznLink[]) => {
      if (term && term !== lastSearchTerm.current) {
        lastSearchTerm.current = term;

        event({
          category: 'Link List',
          action: 'filter link list',
          label: `search_term: ${term}`,
        });
      }

      setVisibleLinks(
        allLinks.filter((link) => {
          if (!byCurrentUser) {
            return link.id.includes(term);
          }
          return link.attributes.authorEmail
            ? link.id.includes(term) && link.attributes.authorEmail.includes(account!.username) // eslint-disable-line @typescript-eslint/no-non-null-assertion
            : false;
        })
      );
    }, DEBOUNCE_MS)
  );

  useEffect(() => {
    debouncedVisibleLinksFilter.current(searchTerm, filterByCurrentUser, links);
  }, [searchTerm, filterByCurrentUser, links]);

  return (
    <>
      <TableContainer component={Paper}>
        {isLoading && <LinearProgress />}
        <Table aria-label="simple table">
          <LinkListHead
            isActiveSearch={isActiveSearch}
            setIsActiveSearch={setIsActiveSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setFilterByCurrentUser={setFilterByCurrentUser}
            filterByCurrentUser={filterByCurrentUser}
          />

          <TableBody>
            {visibleLinks.map((link) => (
              <LinkListRow key={link.id} link={link} setLinkCopied={setLinkCopied} linkCopied={linkCopied} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
